import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Polygon, Popup, Marker, useMapEvent, useMap } from 'react-leaflet';
import { FeatureGroup } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import { getDocs, addDoc, collection, doc, getDoc, query, where, updateDoc, deleteDoc, writeBatch } from "firebase/firestore";
import { db } from '../firebase/config';
import ZoneOptionsModal from './ZoneOptionsModal';
import { 
  SimpleHorseListModal, 
  SimpleHorseDetailsModal
} from './SimpleHorseModal';
import SimpleMoveConfirmationModal from './MoveConfirmationModal';
import ReportProblemModal from './ReportProblemModal';
import MedicalTicketModal from './MedicalTicketModal';
import TechnicalTicketModal from './TechnicalTicketModal';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Edit3, Crosshair, X, MapPin, Clock, User, CheckCircle, PenTool, Copy, Move, Trash2, Save, XCircle, Satellite, Map as MapIcon, Undo, Redo, FolderOpen } from 'lucide-react';
import { FaWrench } from "react-icons/fa6";
import { FaHandHoldingMedical } from "react-icons/fa6";
import { GiHorseHead } from 'react-icons/gi';
import MoveAllHorsesModal from "./MoveAllHorsesModal";
import MoveAllConfirmationModal from "./MoveAllConfirmationModal";

// Configuration des icônes Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Créer une icône personnalisée pour les pastilles de chevaux
const createHorseCountIcon = (count, surveillanceColor = "vert", isHighlighted = false) => {
  let color =
    surveillanceColor === "rouge" ? "#dc2626" :
    surveillanceColor === "orange" ? "#f59e42" :
    surveillanceColor === "jaune" ? "#fde047" :
    "#22c55e"; // vert

  // Si la zone est en surbrillance, ajouter un effet visuel
  const border = isHighlighted ? '4px solid #3b82f6' : '2px solid white';
  const boxShadow = isHighlighted ? '0 0 20px rgba(59, 130, 246, 0.8)' : '0 2px 4px rgba(0,0,0,0.3)';
  const animation = isHighlighted ? 'pulse 2s infinite' : '';

  return L.divIcon({
    html: `
      <div style="
        background-color: ${color};
        color: white;
        border-radius: 50%;
        width: ${isHighlighted ? '40px' : '30px'};
        height: ${isHighlighted ? '40px' : '30px'};
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: ${isHighlighted ? '18px' : '14px'};
        border: ${border};
        box-shadow: ${boxShadow};
        cursor: pointer;
        animation: ${animation};
      ">${count}</div>
      ${isHighlighted ? `
        <style>
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.1); opacity: 0.9; }
            100% { transform: scale(1); opacity: 1; }
          }
        </style>
      ` : ''}
    `,
    className: 'horse-count-marker',
    iconSize: isHighlighted ? [40, 40] : [30, 30],
    iconAnchor: isHighlighted ? [20, 20] : [15, 15],
  });
};

// Créer une icône pour un animal spécifique localisé
const createLocatedAnimalIcon = () => {
  return L.divIcon({
    html: `<div style="
      background-color: #3b82f6;
      color: white;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 3px solid white;
      box-shadow: 0 0 20px rgba(59, 130, 246, 0.8);
      animation: pulse 2s infinite;
    ">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
      </svg>
    </div>
    <style>
      @keyframes pulse {
        0% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.1); opacity: 0.8; }
        100% { transform: scale(1); opacity: 1; }
      }
    </style>`,
    className: 'located-animal-marker',
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
};

// --- Bouton de géolocalisation pour le panneau résumé ---
function GeolocButtonPanel({ mapRef }) {
  const handleGeoloc = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      const { latitude, longitude } = pos.coords;
      if (mapRef.current) {
        mapRef.current.setView([latitude, longitude], 17, { animate: true });
      }
    });
  };

  return (
    <Button 
      variant="outline" 
      className="w-full flex items-center gap-2 justify-center mt-2" 
      onClick={handleGeoloc}
      type="button"
    >
      <Crosshair className="h-4 w-4" />
      Me localiser sur la carte
    </Button>
  );
}


// Composant pour gérer l'édition de polygones
function EditableZone({ zone, editMode, onUpdate, onSave }) {
  const map = useMap();
  const [polygon, setPolygon] = useState(null);
  const [originalCoords, setOriginalCoords] = useState(null);
  const [editHistory, setEditHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  useEffect(() => {
    if (!zone.coordinates || zone.coordinates.length === 0) return;
    
    const coords = getLatLngPairs(zone.coordinates);
    
    // Mode édition des points
    if (editMode === 'edit') {
      const poly = L.polygon(coords, {
        color: '#ff6b6b',
        fillColor: '#ff6b6b',
        fillOpacity: 0.3,
        weight: 3
      });
      
      poly.addTo(map);
      
      // Créer des marqueurs pour chaque point
      const markers = [];
      const updatePolygon = () => {
        const newCoords = markers.map(m => m.getLatLng());
        poly.setLatLngs(newCoords);
        
        // Ajouter à l'historique
        const coordsArray = newCoords.map(ll => [ll.lat, ll.lng]);
        const newHistory = editHistory.slice(0, historyIndex + 1);
        newHistory.push(coordsArray);
        setEditHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
      };
      
      coords.forEach((coord, index) => {
        const marker = L.marker(coord, {
          draggable: true,
          icon: L.divIcon({
            className: 'edit-point-marker',
            html: `<div style="
              width: 12px;
              height: 12px;
              background: white;
              border: 2px solid #ff6b6b;
              border-radius: 50%;
              cursor: move;
            "></div>`,
            iconSize: [12, 12],
            iconAnchor: [6, 6]
          })
        });
        
        marker.on('drag', updatePolygon);
        marker.addTo(map);
        markers.push(marker);
      });
      
      // Sauvegarder l'état initial
      setOriginalCoords(coords);
      setEditHistory([coords]);
      setHistoryIndex(0);
      setPolygon({ poly, markers });
      
      return () => {
        if (poly) map.removeLayer(poly);
        markers.forEach(m => map.removeLayer(m));
      };
    }
    
    // Mode déplacement
    if (editMode === 'move') {
      const poly = L.polygon(coords, {
        color: '#3b82f6',
        fillColor: '#3b82f6',
        fillOpacity: 0.3,
        weight: 3,
        dashArray: '10, 5'
      });
      
      poly.addTo(map);
      
      // Variables pour le drag
      let isDragging = false;
      let dragStartLatLng = null;
      let originalPositions = null;
      
      // Début du drag
      poly.on('mousedown', (e) => {
        isDragging = true;
        dragStartLatLng = e.latlng;
        originalPositions = poly.getLatLngs()[0].map(ll => ({ lat: ll.lat, lng: ll.lng }));
        map.dragging.disable();
        
        // Changer le curseur
        map.getContainer().style.cursor = 'move';
      });
      
      // Mouvement de la souris
      const onMouseMove = (e) => {
        if (!isDragging || !dragStartLatLng || !originalPositions) return;
        
        const deltaLat = e.latlng.lat - dragStartLatLng.lat;
        const deltaLng = e.latlng.lng - dragStartLatLng.lng;
        
        const newCoords = originalPositions.map(pos => 
          L.latLng(pos.lat + deltaLat, pos.lng + deltaLng)
        );
        
        poly.setLatLngs(newCoords);
      };
      
      // Fin du drag
      const onMouseUp = (e) => {
        if (!isDragging) return;
        
        isDragging = false;
        map.dragging.enable();
        map.getContainer().style.cursor = '';
        
        // Sauvegarder les nouvelles coordonnées
        const finalCoords = poly.getLatLngs()[0].map(ll => ({ lat: ll.lat, lng: ll.lng }));
        onSave(zone.id, { coordinates: finalCoords });
      };
      
      map.on('mousemove', onMouseMove);
      map.on('mouseup', onMouseUp);
      
      setPolygon(poly);
      
      return () => {
        if (poly) map.removeLayer(poly);
        map.off('mousemove', onMouseMove);
        map.off('mouseup', onMouseUp);
        map.dragging.enable();
        map.getContainer().style.cursor = '';
      };
    }
  }, [zone, editMode, map]);
  
  // Fonctions Undo/Redo (pour le mode édition de points)
  const handleUndo = () => {
    if (historyIndex > 0 && polygon) {
      const newIndex = historyIndex - 1;
      const coords = editHistory[newIndex];
      
      if (editMode === 'edit' && polygon.markers) {
        // Mettre à jour les positions des marqueurs
        coords.forEach((coord, i) => {
          if (polygon.markers[i]) {
            polygon.markers[i].setLatLng(coord);
          }
        });
        polygon.poly.setLatLngs(coords);
      }
      
      setHistoryIndex(newIndex);
    }
  };
  
  const handleRedo = () => {
    if (historyIndex < editHistory.length - 1 && polygon) {
      const newIndex = historyIndex + 1;
      const coords = editHistory[newIndex];
      
      if (editMode === 'edit' && polygon.markers) {
        // Mettre à jour les positions des marqueurs
        coords.forEach((coord, i) => {
          if (polygon.markers[i]) {
            polygon.markers[i].setLatLng(coord);
          }
        });
        polygon.poly.setLatLngs(coords);
      }
      
      setHistoryIndex(newIndex);
    }
  };
  
  // Afficher les contrôles pour le mode édition
  if (editMode === 'edit' && polygon) {
    return (
      <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-[700] bg-white rounded-lg shadow-xl p-3 flex gap-2">
        <button
          onClick={handleUndo}
          disabled={historyIndex <= 0}
          className="p-2 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
          title="Annuler"
        >
          <Undo className="h-4 w-4" />
        </button>
        <button
          onClick={handleRedo}
          disabled={historyIndex >= editHistory.length - 1}
          className="p-2 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
          title="Refaire"
        >
          <Redo className="h-4 w-4" />
        </button>
        <button
          onClick={() => {
            const finalCoords = polygon.markers.map(m => {
              const ll = m.getLatLng();
              return { lat: ll.lat, lng: ll.lng };
            });
            onSave(zone.id, { coordinates: finalCoords });
          }}
          className="px-4 py-2 rounded bg-green-500 text-white hover:bg-green-600"
        >
          <Save className="h-4 w-4 inline mr-2" />
          Sauvegarder
        </button>
        <button
          onClick={() => {
            if (originalCoords && polygon.markers) {
              originalCoords.forEach((coord, i) => {
                if (polygon.markers[i]) {
                  polygon.markers[i].setLatLng(coord);
                }
              });
              polygon.poly.setLatLngs(originalCoords);
            }
            onSave(null);
          }}
          className="px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600"
        >
          <X className="h-4 w-4 inline mr-2" />
          Annuler
        </button>
      </div>
    );
  }
  
  return null;
}




// Composant pour capturer les clics GPS sur la carte
function GPSClickHandler({ selectingGPSLocation, setTechnicalGPSLocation, setSelectingGPSLocation, setShowTechnicalTicket, pendingTechnicalTicket, setPendingTechnicalTicket, navigate }) {
  useMapEvent('click', async (e) => {
    if (selectingGPSLocation) {
      const { lat, lng } = e.latlng;
      
      // Si on vient du tableau de bord
      if (pendingTechnicalTicket?.fromDashboard) {
        // Créer directement le ticket ici avec les coordonnées GPS
        try {
          const now = new Date().toISOString();
          const newTicket = {
            type: 'technique',
            subType: pendingTechnicalTicket.subType,
            title: pendingTechnicalTicket.title,
            message: pendingTechnicalTicket.description,
            priority: pendingTechnicalTicket.priority,
            gpsLocation: {
              latitude: lat,
              longitude: lng
            },
            dateCreation: now,
            dateModification: now,
            acteur: 'Utilisateur',
            statut: 'ouvert',
            historique: [{
              date: now,
              acteur: 'Utilisateur',
              action: 'creation',
              message: `Création du ticket technique - ${pendingTechnicalTicket.subType}`
            }]
          };
          
          await addDoc(collection(db, 'tickets'), newTicket);
          
          // Afficher une confirmation rapide
          L.popup()
            .setLatLng([lat, lng])
            .setContent(`✅ Ticket technique créé avec succès`)
            .openOn(e.target);
          
          setTimeout(() => {
            // Retourner au tableau de bord
            navigate('/tableau-bord');
          }, 1500);
          
        } catch (error) {
          console.error('Erreur lors de la création du ticket:', error);
          alert('Erreur lors de la création du ticket');
        }
        
        // Réinitialiser les états
        setSelectingGPSLocation(false);
        setPendingTechnicalTicket(null);
        
      } else {
        // Comportement normal depuis la carte
        setTechnicalGPSLocation({ lat, lng });
        setSelectingGPSLocation(false);
        setShowTechnicalTicket(true);
        
        // Afficher un marqueur temporaire
        L.popup()
          .setLatLng([lat, lng])
          .setContent(`📍 Position GPS sélectionnée<br/>Lat: ${lat.toFixed(6)}<br/>Lng: ${lng.toFixed(6)}`)
          .openOn(e.target)
          .on('remove', () => {});
        
        setTimeout(() => {
          e.target.closePopup();
        }, 3000);
      }
    }
  });
  return null;
}

// Utilitaires coords
function flattenLatLngs(latlngs) {
  if (
    Array.isArray(latlngs) &&
    latlngs.length &&
    Array.isArray(latlngs[0]) &&
    latlngs[0].length &&
    typeof latlngs[0][0]?.lat === "number"
  ) {
    return latlngs[0].map(ll => [ll.lat, ll.lng]);
  }
  if (
    Array.isArray(latlngs) &&
    latlngs.length &&
    typeof latlngs[0]?.lat === "number"
  ) {
    return latlngs.map(ll => [ll.lat, ll.lng]);
  }
  return [];
}
function getLatLngPairs(coords) {
  return (coords || [])
    .filter(pt => pt && typeof pt.lat === "number" && typeof pt.lng === "number" && !isNaN(pt.lat) && !isNaN(pt.lng))
    .map(pt => [pt.lat, pt.lng]);
}

const HorseMap = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [horses, setHorses] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedHorse, setSelectedHorse] = useState(null);
  const [showHorseList, setShowHorseList] = useState(false);
  const [showHorseDetails, setShowHorseDetails] = useState(false);
  const [showMoveConfirmation, setShowMoveConfirmation] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showZoneActionModal, setShowZoneActionModal] = useState(false);
  const [zoneActionTarget, setZoneActionTarget] = useState(null);
  const [moveFromMapMode, setMoveFromMapMode] = useState(false);
  const [pendingHorseToMove, setPendingHorseToMove] = useState(null);
  const [moveAllMode, setMoveAllMode] = useState(false);
  const [moveAllFromMapMode, setMoveAllFromMapMode] = useState(false);
  const [showMoveAllModal, setShowMoveAllModal] = useState(false);
  const [pendingAllMove, setPendingAllMove] = useState(null);
  const [moveTargetZone, setMoveTargetZone] = useState(null);
  const [showMoveAllConfirmation, setShowMoveAllConfirmation] = useState(false);
  const [pendingAllTargetZone, setPendingAllTargetZone] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showReportProblem, setShowReportProblem] = useState(false);
  const [horseToReport, setHorseToReport] = useState(null);
  
  // États pour le modal médical
  const [showMedicalTicket, setShowMedicalTicket] = useState(false);
  const [horseForMedical, setHorseForMedical] = useState(null);
  
  // États pour les tickets techniques
  const [showTechnicalTicket, setShowTechnicalTicket] = useState(false);
  const [technicalTicketZone, setTechnicalTicketZone] = useState(null);
  const [selectingTechnicalLocation, setSelectingTechnicalLocation] = useState(false);
  const [pendingTechnicalTicket, setPendingTechnicalTicket] = useState(null);
  
  // États pour la sélection GPS
  const [selectingGPSLocation, setSelectingGPSLocation] = useState(false);
  const [technicalGPSLocation, setTechnicalGPSLocation] = useState(null);
  
  // États pour la localisation depuis le tableau de bord
  const [highlightedZone, setHighlightedZone] = useState(null);
  const [locatedAnimal, setLocatedAnimal] = useState(null);
  const [ticketInfo, setTicketInfo] = useState(null);

  // États pour les tickets techniques permanents
  const [technicalTickets, setTechnicalTickets] = useState([]);
  // États pour les tickets médicaux permanents  
  const [medicalTickets, setMedicalTickets] = useState([]);
  const [showMedicalTicketDetail, setShowMedicalTicketDetail] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // État pour le ticket technique sélectionné
  const [selectedTechnicalTicket, setSelectedTechnicalTicket] = useState(null);
  const [showTechnicalTicketDetail, setShowTechnicalTicketDetail] = useState(false);

  // NOUVEAUX ÉTATS POUR LE MODE ÉDITION
  const [editMode, setEditMode] = useState(null); // null, 'draw', 'edit', 'duplicate', 'move', 'delete'
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [selectedEditZone, setSelectedEditZone] = useState(null);
  const [duplicatingZone, setDuplicatingZone] = useState(null);
  const [editingZones, setEditingZones] = useState([]);
// État pour le mode satellite
  const [isSatellite, setIsSatellite] = useState(false);
  // Référence carte
  const mapRef = useRef(null);
  const featureGroupRef = useRef(null);
  
  // Debug - surveiller l'état du modal de signalement
  useEffect(() => {
    console.log('Report Problem Modal State:', {
      showReportProblem,
      horseToReport: horseToReport?.nom || 'none'
    });
  }, [showReportProblem, horseToReport]);

  // Fonction globale pour ouvrir le modal de signalement depuis SimpleHorseDetailsModal
  // IMPORTANT: Doit être défini AVANT le montage des composants enfants
  useEffect(() => {
    console.log('Setting up window functions...');
    
    // Fonction pour "Signaler problème" - ouvre ReportProblemModal
    window.onOpenReportProblem = (horse) => {
      console.log('=== window.onOpenReportProblem CALLED ===');
      console.log('Horse received:', horse);
      
      setHorseToReport(horse);
      setShowReportProblem(true);
      setShowHorseDetails(false);
      setShowHorseList(false);
    };
    
    // Fonction pour "Ticket médical" - ouvre MedicalTicketModal
    window.onOpenMedicalTicket = (horse) => {
      console.log('=== window.onOpenMedicalTicket CALLED ===');
      console.log('Horse received:', horse);
      
      setHorseForMedical(horse);
      setShowMedicalTicket(true);
      setShowHorseDetails(false);
      setShowHorseList(false);
    };
    
    // Vérifier que c'est bien attaché
    console.log('window.onOpenReportProblem type:', typeof window.onOpenReportProblem);
    console.log('window.onOpenMedicalTicket type:', typeof window.onOpenMedicalTicket);
    
    return () => {
      delete window.onOpenReportProblem;
      delete window.onOpenMedicalTicket;
    };
  }, []); // Pas de dépendances pour éviter de recréer

  useEffect(() => {
    loadData();
  }, []);

  // Effet pour gérer la localisation depuis le tableau de bord
  useEffect(() => {
    // Localisation d'un animal
    if (location.state?.focusAnimalId && horses.length > 0 && zones.length > 0) {
      const animalId = location.state.focusAnimalId;
      const ticket = location.state.ticketInfo;
      
      // Chercher l'animal dans les données chargées
      const animal = horses.find(h => h.id === animalId);
      
      if (animal && animal.zoneId) {
        // Trouver la zone de l'animal
        const zone = zones.find(z => z.id === animal.zoneId);
        
        if (zone) {
          // Mettre la zone en surbrillance
          setHighlightedZone(zone.id);
          
          // Centrer la carte sur la zone (comme quand on clique dans le menu)
          flyToZone(zone);
          
          // Stocker les infos pour la notification
          setLocatedAnimal(animal);
          setTicketInfo(ticket);
          
          // Retirer la surbrillance après 10 secondes
          setTimeout(() => {
            setHighlightedZone(null);
            setLocatedAnimal(null);
            setTicketInfo(null);
          }, 10000);
        } else {
          alert(`L'animal "${animal.nom}" n'est assigné à aucune zone.`);
        }
      } else {
        console.error('Animal non trouvé avec l\'ID:', animalId);
        alert('Animal non trouvé dans la base de données');
      }
      
      // Nettoyer le state de navigation
      window.history.replaceState({}, document.title);
    }
    
    // Localisation GPS technique
    if (location.state?.focusTechnicalGPS && mapRef.current) {
      const { lat, lng } = location.state.focusTechnicalGPS;
      const ticket = location.state.ticketInfo;
      
      // Centrer la carte sur les coordonnées GPS
      setTimeout(() => {
        mapRef.current.setView([lat, lng], 18, { animate: true });
        
        // Afficher une popup
        L.popup()
          .setLatLng([lat, lng])
          .setContent(`
            <div style="min-width: 200px;">
              <h3 style="font-weight: bold; margin-bottom: 8px;">🔧 ${ticket.title}</h3>
              <div style="background: #f3f4f6; padding: 8px; border-radius: 4px;">
                <p style="font-size: 12px; color: #6b7280; margin-bottom: 4px;">
                  Ticket Technique - ${ticket.priority}
                </p>
                ${ticket.message ? `<p style="font-size: 12px; margin-top: 4px; color: #4b5563;">${ticket.message}</p>` : ''}
              </div>
            </div>
          `)
          .openOn(mapRef.current);
          
        // Stocker temporairement pour afficher le marqueur
        setTechnicalGPSLocation({ lat, lng });
        setTicketInfo(ticket);
        
        setTimeout(() => {
          setTechnicalGPSLocation(null);
          setTicketInfo(null);
        }, 10000);
      }, 500);
      
      // Nettoyer le state de navigation
      window.history.replaceState({}, document.title);
    }
    
    // Localisation par nom de zone (pour tickets techniques)
    if (location.state?.focusZoneName && zones.length > 0) {
      const zoneName = location.state.focusZoneName;
      const ticket = location.state.ticketInfo;
      
      const zone = zones.find(z => z.name === zoneName);
      if (zone) {
        setHighlightedZone(zone.id);
        flyToZone(zone);
        setTicketInfo(ticket);
        
        setTimeout(() => {
          setHighlightedZone(null);
          setTicketInfo(null);
        }, 10000);
      }
      
      // Nettoyer le state de navigation
      window.history.replaceState({}, document.title);
    }
    // Sélection GPS pour ticket technique depuis tableau de bord
    if (location.state?.selectingGPSForTechnical && location.state?.technicalTicketData) {
      // L'utilisateur vient du tableau de bord pour sélectionner un GPS
      setPendingTechnicalTicket(location.state.technicalTicketData);
      setSelectingGPSLocation(true);
      
      // Nettoyer le state
      window.history.replaceState({}, document.title);
    }

  }, [location.state, horses, zones]);

// Effet pour gérer le paramètre zone depuis l'URL (depuis la page Zones)
  useEffect(() => {
    const zoneId = searchParams.get('zone');
    
    if (zoneId && zones.length > 0) {
      // Trouver la zone correspondante
      const targetZone = zones.find(z => z.id === zoneId);
      
      if (targetZone) {
        // Mettre en surbrillance temporairement
        setHighlightedZone(targetZone.id);
        
        // Centrer la carte sur cette zone
        flyToZone(targetZone);
        
        // Optionnel : afficher une notification
        if (mapRef.current) {
          setTimeout(() => {
            L.popup()
              .setLatLng(getZoneCenter(getLatLngPairs(targetZone.coordinates)))
              .setContent(`📍 Zone: ${targetZone.name}`)
              .openOn(mapRef.current);
          }, 500);
        }
        
        // Retirer la surbrillance après 5 secondes
        setTimeout(() => {
          setHighlightedZone(null);
          if (mapRef.current) {
            mapRef.current.closePopup();
          }
        }, 5000);
        
        // Nettoyer l'URL pour éviter de re-déclencher
        navigate('/carte', { replace: true });
      }
    }
  }, [searchParams, zones, navigate]);


  
  const loadData = async () => {
    try {
      const chevauxSnap = await getDocs(collection(db, "animaux"));
      let horsesData = [];
      chevauxSnap.forEach(docu => {
        const d = docu.data();
        horsesData.push({ id: docu.id, ...d });
      });

      const zonesSnap = await getDocs(collection(db, "lieux"));
      let zonesData = [];
      zonesSnap.forEach(docu => {
        const d = docu.data();
        // Ajouter les champs par défaut s'ils n'existent pas
        zonesData.push({ 
          id: docu.id, 
          ...d,
          level: d.level ?? 0,
          order: d.order ?? 999,
          parentId: d.parentId ?? null,
    isOccupable: d.isOccupable ?? true,  // <-- VIRGULE ICI
          isVirtual: d.isVirtual ?? false  // <-- AJOUTEZ CETTE LIGNE
        });
      });
      
       // Trier les zones selon l'ordre hiérarchique
      zonesData.sort((a, b) => {
        // D'abord par niveau (parents avant enfants dans le tableau)
        if (a.level !== b.level) return a.level - b.level;
        // Puis par ordre
        return a.order - b.order;
      });

      setHorses(horsesData);
      setZones(zonesData);
      
      // Charger les tickets techniques ouverts (GPS ou Zone)
      try {
        const ticketsQuery = query(
          collection(db, "tickets"),
          where("statut", "==", "ouvert"),
          where("type", "==", "technique")
        );
        const ticketsSnap = await getDocs(ticketsQuery);
        const ticketsData = [];
        ticketsSnap.forEach(docu => {
          const data = docu.data();
          
          // Tickets avec GPS
          if (data.gpsLocation) {
            ticketsData.push({ 
              id: docu.id, 
              ...data,
              locationType: 'gps',
              gps: {
                lat: data.gpsLocation.latitude,
                lng: data.gpsLocation.longitude
              }
            });
          }
          // Tickets avec Zone
          else if (data.zoneId) {
            // Trouver la zone pour obtenir ses coordonnées
            const zone = zonesData.find(z => z.id === data.zoneId);
            if (zone && zone.coordinates) {
              // Placer le marqueur au centre de la zone, légèrement décalé
              const center = getZoneCenter(getLatLngPairs(zone.coordinates));
              ticketsData.push({ 
                id: docu.id, 
                ...data,
                locationType: 'zone',
                zoneId: data.zoneId,
                zoneName: data.zoneName,
                gps: {
                  lat: center[0] - 0.00008, // Décaler légèrement vers le bas
                  lng: center[1] + 0.00008  // Et légèrement vers la droite
                }
              });
            }
          }
        });
        setTechnicalTickets(ticketsData);
        console.log('Tickets techniques chargés:', ticketsData);
      } catch (error) {
        console.log('Pas de tickets techniques ou erreur:', error);
      }
      
      // Charger les tickets médicaux ouverts
      try {
        const medicalQuery = query(
          collection(db, "tickets"),
          where("statut", "==", "ouvert"),
          where("type", "==", "medical")
        );
        const medicalSnap = await getDocs(medicalQuery);
        const medicalData = [];
        
        medicalSnap.forEach(docu => {
          const data = docu.data();
          
          // Si on a l'ID directement (nouveaux tickets)
          let animal;
          if (data.horseId) {
            animal = horsesData.find(h => h.id === data.horseId);
          } else {
            // Sinon chercher par nom (anciens tickets)
            animal = horsesData.find(h => h.nom === data.horseName);
          }
          
          if (animal && animal.zoneId) {
            // Trouver la zone de l'animal
            const zone = zonesData.find(z => z.id === animal.zoneId);
            
            if (zone && zone.coordinates) {
              // Calculer la position du marqueur (centre de zone, décalé différemment du technique)
              const center = getZoneCenter(getLatLngPairs(zone.coordinates));
              medicalData.push({
                id: docu.id,
                ...data,
                animalId: animal.id,
                currentZoneId: animal.zoneId,
                currentZoneName: zone.name,
                gps: {
                  lat: center[0] + 0.00008, // Décaler vers le haut
                  lng: center[1] - 0.00008  // Et légèrement vers la gauche
                }
              });
            }
          }
        });
        
        setMedicalTickets(medicalData);
        console.log('Tickets médicaux chargés:', medicalData);
      } catch (error) {
        console.log('Erreur chargement tickets médicaux:', error);
      }

    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    }
  };

  const handleZoneBadgeClick = (zone) => {
    setSelectedZone(zone);
    setShowHorseList(true);
  };

  const handleHorseSelect = (horse) => {
    setSelectedHorse(horse);
    setShowHorseDetails(true);
    setShowHorseList(false);
  };

  // Fonction directe pour ouvrir le modal de signalement
  const handleReportProblemClick = (horse) => {
    console.log('Direct report problem click for:', horse);
    setShowHorseDetails(false);
    setShowHorseList(false);
    setHorseToReport(horse || selectedHorse);
    setTimeout(() => {
      setShowReportProblem(true);
    }, 200);
  };

  const handleMoveHorse = (targetZoneId) => {
    setMoveTargetZone(zones.find(z => z.id === targetZoneId));
    setShowMoveConfirmation(true);
    setShowHorseDetails(false);
  };

  const confirmMoveHorse = async () => {
    if ((selectedHorse || pendingHorseToMove) && moveTargetZone) {
      const horseToMove = selectedHorse || pendingHorseToMove;
      try {
        const horseRef = doc(db, "animaux", horseToMove.id);
        await updateDoc(horseRef, { zoneId: moveTargetZone.id });
        loadData();
        setShowMoveConfirmation(false);
        setSelectedHorse(null);
        setMoveTargetZone(null);
        setPendingHorseToMove(null);
        setMoveFromMapMode(false);
      } catch (error) {
        console.error('Erreur lors du déplacement:', error);
      }
    }
  };

  const handleDrawCreated = async (e) => {
    const { layer } = e;
    const coordinates = flattenLatLngs(layer.getLatLngs()).map(([lat, lng]) => ({ lat, lng }));
    const name = prompt("Nom de la nouvelle zone ?");
    if (!name) return;
    
    // Trouver l'ordre maximum actuel
    const maxOrder = Math.max(...zones.map(z => z.order || 0), -1);
    
    const newZone = {
      name: name,
      type: 'prairie',
      coordinates,
      capacity: 10,
      color: '#22c55e',
      level: 0,
      order: maxOrder + 1,
      parentId: null,
      isOccupable: true
    };
    
    try {
      await addDoc(collection(db, "lieux"), newZone);
      loadData();
    } catch (error) {
      console.error('Erreur lors de la création de zone:', error);
    }
  };

  const getZoneCenter = (coordinates) => {
    const lats = coordinates.map(coord => coord[0]);
    const lngs = coordinates.map(coord => coord[1]);
    return [
      lats.reduce((a, b) => a + b) / lats.length,
      lngs.reduce((a, b) => a + b) / lngs.length
    ];
  };

  const handleMoveFromMap = (horse) => {
    setPendingHorseToMove(horse);
    setMoveFromMapMode(true);
    setShowHorseDetails(false);
    setShowMoveConfirmation(false);
    setMoveTargetZone(null);
  };

  const handlePolygonClick = (zone) => {
    // Mode édition - sélectionner la zone pour l'action
if (editMode === 'edit' || editMode === 'move') {
      setSelectedEditZone(zone);
      return;
    }
    
    if (editMode === 'duplicate') {
      handleDuplicateZone(zone);
      // Fermer immédiatement le mode duplication après avoir dupliqué
      setEditMode(null);
      return;
    }
    
    if (editMode === 'delete') {
      handleDeleteZone(zone);
      return;
    }
    
    // Mode sélection de localisation pour ticket technique
    if (selectingTechnicalLocation && pendingTechnicalTicket) {
      setTechnicalTicketZone(zone);
      setShowTechnicalTicket(true);
      setSelectingTechnicalLocation(false);
      return;
    }
    
    // Mode sélection GPS - ne pas ouvrir le menu zone
    if (selectingGPSLocation) {
      return; // On ne fait rien sur le clic de zone en mode GPS
    }
    
    // Modes de déplacement existants
    if (moveFromMapMode && pendingHorseToMove && zone.id !== pendingHorseToMove.zoneId) {
      setMoveTargetZone(zone);
      setShowMoveConfirmation(true);
      setMoveFromMapMode(false);
      return;
    }
    if (moveAllFromMapMode && pendingAllMove && zone.id !== pendingAllMove.id) {
      setMoveTargetZone(zone);
      setShowMoveAllConfirmation(true);
      setMoveAllFromMapMode(false);
      return;
    }
    
    // Ouvrir le menu d'options de zone
    setZoneActionTarget(zone);
    setShowZoneActionModal(true);
  };

  // NOUVELLES FONCTIONS POUR LE MODE ÉDITION
  const handleEditModeChange = (mode) => {
    setEditMode(mode);
    setSelectedEditZone(null);
    
    if (mode === 'draw') {
      setIsDrawing(true);
    } else {
      setIsDrawing(false);
    }
  };

  const handleDuplicateZone = async (zone) => {
    const name = prompt(`Nom de la copie de "${zone.name}" ?`, `${zone.name} - Copie`);
    if (!name) return;
    
    // Créer une copie avec un léger décalage
    const offset = 0.0002; // Environ 20m
    const newCoordinates = zone.coordinates.map(coord => ({
      lat: coord.lat + offset,
      lng: coord.lng + offset
    }));
    
    const maxOrder = Math.max(...zones.map(z => z.order || 0), -1);
    
    const newZone = {
      ...zone,
      name: name,
      coordinates: newCoordinates,
      order: maxOrder + 1
    };
    
    // Retirer l'ID pour créer une nouvelle zone
    delete newZone.id;
    
    try {
      await addDoc(collection(db, "lieux"), newZone);
      loadData();
      alert(`Zone "${name}" créée avec succès`);
    } catch (error) {
      console.error('Erreur lors de la duplication:', error);
      alert('Erreur lors de la duplication de la zone');
    }
  };

  const handleDeleteZone = async (zone) => {
    // Vérifier si la zone contient des animaux
    const animalsInZone = horses.filter(h => h.zoneId === zone.id);
    if (animalsInZone.length > 0) {
      alert(`Impossible de supprimer la zone "${zone.name}" : elle contient ${animalsInZone.length} animal(aux). Déplacez d'abord les animaux.`);
      return;
    }
    
    // Vérifier si la zone a des enfants
    const childZones = zones.filter(z => z.parentId === zone.id);
    
    if (childZones.length > 0) {
      const confirmDelete = confirm(
        `La zone "${zone.name}" a ${childZones.length} zone(s) enfant(s). ` +
        `Voulez-vous supprimer la zone parent ? Les zones enfants remonteront d'un niveau dans la hiérarchie.`
      );
      
      if (!confirmDelete) return;
      
      // Mettre à jour les enfants pour remonter d'un niveau
      const batch = writeBatch(db);
      
      childZones.forEach(child => {
        const childRef = doc(db, "lieux", child.id);
        batch.update(childRef, {
          parentId: zone.parentId, // Le parent du parent devient le nouveau parent
          level: Math.max(0, child.level - 1) // Remonter d'un niveau
        });
      });
      
      // Supprimer la zone
      batch.delete(doc(db, "lieux", zone.id));
      
      try {
        await batch.commit();
        loadData();
        alert(`Zone "${zone.name}" supprimée avec succès`);
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression de la zone');
      }
    } else {
      // Pas d'enfants, suppression simple
      const confirmDelete = confirm(`Êtes-vous sûr de vouloir supprimer la zone "${zone.name}" ?`);
      if (!confirmDelete) return;
      
      try {
        await deleteDoc(doc(db, "lieux", zone.id));
        loadData();
        alert(`Zone "${zone.name}" supprimée avec succès`);
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression de la zone');
      }
    }
  };

  const handleExitEditMode = () => {
    setEditMode(null);
    setShowEditPanel(false);
    setIsDrawing(false);
    setSelectedEditZone(null);
  };

// Fonction pour sauvegarder les modifications d'une zone
  const handleSaveZoneEdit = async (zoneId, updates) => {
    if (!zoneId || !updates) {
      // Annulation
      setSelectedEditZone(null);
      setEditMode(null);
      return;
    }
    
    try {
      const zoneRef = doc(db, "lieux", zoneId);
      await updateDoc(zoneRef, updates);
      loadData();
      setSelectedEditZone(null);
      setEditMode(null);
      alert('Zone modifiée avec succès');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde de la zone');
    }
  };

  // Gestionnaires pour les incidents
  const handleReportTechnicalIncident = (zone) => {
    setTechnicalTicketZone(zone);
    setShowTechnicalTicket(true);
    setShowZoneActionModal(false); // Fermer le modal de zone
  };

  const handleReportAnimalIncident = (zone) => {
    // Ouvrir la liste des animaux pour sélectionner celui concerné
    setSelectedZone(zone);
    setShowHorseList(true);
    setShowZoneActionModal(false); // Fermer le modal de zone
  };

  const handleSelectTechnicalLocationFromMap = (ticketState) => {
    setPendingTechnicalTicket(ticketState);
    setSelectingTechnicalLocation(true);
    setShowTechnicalTicket(false);
  };

  const handleSelectGPSLocation = (ticketState) => {
    setPendingTechnicalTicket(ticketState);
    setSelectingGPSLocation(true);
    setShowTechnicalTicket(false);
  };

// Construire l'arbre hiérarchique des zones
  const buildZoneTree = () => {
    const tree = [];
    const zoneMap = {};
    
    // Créer une map pour accès rapide
    zones.forEach(zone => {
      zoneMap[zone.id] = { ...zone, children: [] };
    });
    
    // Construire l'arbre
    zones.forEach(zone => {
      if (zone.parentId && zoneMap[zone.parentId]) {
        zoneMap[zone.parentId].children.push(zoneMap[zone.id]);
      } else if (!zone.parentId) {
        tree.push(zoneMap[zone.id]);
      }
    });
    
    return tree;
  };

  // Composant récursif pour afficher l'arbre
  const ZoneTreeItem = ({ zone, level = 0 }) => {
    const horsesCount = getHorsesInZone(zone.id).length;
    const pastilleColor = getZoneSurveillanceColor(zone.id);
    const canClick = !zone.isVirtual && zone.coordinates && zone.coordinates.length > 0;
    
    return (
      <>
        <div
          className={`flex justify-between items-center py-1 ${
            canClick ? 'cursor-pointer hover:bg-blue-50' : 'cursor-default'
          } rounded`}
          onClick={() => canClick && flyToZone(zone)}
          style={{ paddingLeft: `${level * 16}px` }}
          tabIndex={canClick ? 0 : -1}
          role={canClick ? "button" : ""}
          title={canClick ? `Centrer sur ${zone.name}` : zone.name}
        >
          <div className="flex items-center gap-2">
            {zone.isVirtual && (
              <FolderOpen className="w-3 h-3 text-gray-400" />
            )}
            {pastilleColor && !zone.isVirtual && (
              <div
                className="w-3 h-3 rounded-full"
                style={{
                  backgroundColor:
                    pastilleColor === "rouge" ? "#dc2626" :
                    pastilleColor === "orange" ? "#f59e42" :
                    pastilleColor === "jaune" ? "#fde047" :
                    "#22c55e"
                }}
              />
            )}
            <span className={`text-sm ${zone.isVirtual ? 'italic text-gray-500' : ''}`}>
              {zone.name}
            </span>
          </div>
          {!zone.isVirtual && (
            <Badge variant="outline">
              {horsesCount}
            </Badge>
          )}
        </div>
        {zone.children && zone.children.map(child => (
          <ZoneTreeItem key={child.id} zone={child} level={level + 1} />
        ))}
      </>
    );
  };

  const MovingHorseOverlay = () => {
    if (moveFromMapMode && pendingHorseToMove) {
      return (
        <div className="fixed top-0 left-0 right-0 flex justify-center z-[1000] pointer-events-none">
          <div className="mt-8 px-8 py-4 bg-yellow-300 text-xl font-bold rounded-2xl shadow-2xl animate-pulse border-4 border-yellow-400">
            <span>Déplacement : </span>
            <span className="animate-pulse">{pendingHorseToMove.name}</span>
            <span className="ml-4 text-base font-normal text-gray-800">(cliquez sur une zone d'arrivée)</span>
          </div>
        </div>
      );
    }
    
    if (selectingTechnicalLocation) {
      return (
        <div className="fixed top-0 left-0 right-0 flex justify-center z-[1000] pointer-events-none">
          <div className="mt-8 px-8 py-4 bg-purple-300 text-xl font-bold rounded-2xl shadow-2xl animate-pulse border-4 border-purple-400">
            <span>Sélection localisation ticket technique</span>
            <span className="ml-4 text-base font-normal text-gray-800">(cliquez sur une zone)</span>
            <button
              className="ml-6 px-4 py-2 text-base bg-white rounded border border-gray-400 font-normal shadow hover:bg-gray-100 hover:border-gray-600 transition pointer-events-auto"
              onClick={() => {
                setSelectingTechnicalLocation(false);
                setPendingTechnicalTicket(null);
                setShowTechnicalTicket(true);
              }}
              type="button"
            >
              Annuler
            </button>
          </div>
        </div>
      );
    }
    
    if (selectingGPSLocation) {
      return (
        <div className="fixed top-0 left-0 right-0 flex justify-center z-[1000] pointer-events-none">
          <div className="mt-8 px-8 py-4 bg-blue-300 text-xl font-bold rounded-2xl shadow-2xl animate-pulse border-4 border-blue-400">
            <span>📍 Sélection GPS précise</span>
            <span className="ml-4 text-base font-normal text-gray-800">(cliquez n'importe où sur la carte)</span>
            <button
              className="ml-6 px-4 py-2 text-base bg-white rounded border border-gray-400 font-normal shadow hover:bg-gray-100 hover:border-gray-600 transition pointer-events-auto"
              onClick={() => {
                setSelectingGPSLocation(false);
                setPendingTechnicalTicket(null);
                setShowTechnicalTicket(true);
              }}
              type="button"
            >
              Annuler
            </button>
          </div>
        </div>
      );
    }
    
    // Overlay pour le mode édition
    if (editMode && editMode !== 'draw') {
      const modeText = 
        editMode === 'edit' ? 'Édition de zone - Cliquez sur une zone pour modifier ses points' :
        editMode === 'duplicate' ? 'Duplication - Cliquez sur la zone à dupliquer' :
        editMode === 'move' ? 'Déplacement - Cliquez sur la zone à déplacer' :
        editMode === 'delete' ? 'Suppression - Cliquez sur la zone à supprimer' : '';
      
      return (
        <div className="fixed top-0 left-0 right-0 flex justify-center z-[1000] pointer-events-none">
          <div className="mt-8 px-8 py-4 bg-orange-300 text-xl font-bold rounded-2xl shadow-2xl animate-pulse border-4 border-orange-400">
            <span>{modeText}</span>
          </div>
        </div>
      );
    }
    
    return null;
  };

  const flyToZone = (zone) => {
    if (mapRef.current && zone.coordinates && zone.coordinates.length > 0) {
      const bounds = L.latLngBounds(zone.coordinates);
      mapRef.current.fitBounds(bounds, { maxZoom: 18, padding: [40, 40] });
    }
  };

  function MapWithRefSync(props) {
    const map = useMapEvent('load', () => {});
    useEffect(() => {
      if (map && mapRef) mapRef.current = map;
    }, [map]);
    return props.children || null;
  }

  const handleMapCreated = (mapInstance) => {
    mapRef.current = mapInstance;
  };

  function getHorsesInZone(zoneId) {
    return horses.filter(h => h.zoneId === zoneId);
  }
  
  function getZoneSurveillanceColor(zoneId) {
    const chevaux = horses.filter(h => h.zoneId === zoneId);
    if (chevaux.length === 0) return null;
    const gravite = ["vert", "jaune", "orange", "rouge"];
    const etats = chevaux.map(h => h.SurveillanceEtat ? h.SurveillanceEtat.toLowerCase() : "vert");
    let couleur = "vert";
    for (let g = gravite.length - 1; g >= 0; g--) {
      if (etats.includes(gravite[g])) {
        couleur = gravite[g];
        break;
      }
    }
    return couleur;
  }

  // Notification de localisation active
  const LocationNotification = () => {
    if (!locatedAnimal || !ticketInfo) return null;
    
    const zone = zones.find(z => z.id === locatedAnimal.zoneId);
    
    return (
      <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-[1000] bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
        <svg className="w-5 h-5 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
        </svg>
        <div>
          <div className="font-semibold">Localisation: {locatedAnimal.nom}</div>
          <div className="text-sm opacity-90">
            {zone ? `Zone: ${zone.name} - ` : ''}
            {ticketInfo.type === 'signalement' ? 'Signalement' : 'Intervention médicale'}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full h-full relative">
      {/* Bouton Mode Édition principal */}
      <div className="absolute top-3 z-[500]" style={{ left: 52 }}>
        <Button
          variant={showEditPanel ? "default" : "outline"}
          onClick={() => setShowEditPanel(!showEditPanel)}
          className="flex items-center gap-2"
          type="button"
        >
          <Edit3 className="h-4 w-4" />
          {showEditPanel ? 'Fermer édition' : 'Mode édition'}
        </Button>
      </div>
      
      {/* Panneau d'édition */}
      {showEditPanel && (
        <div className="absolute top-14 left-12 z-[600] bg-white rounded-lg shadow-xl p-4 min-w-[200px]">
          <div className="space-y-2">
            <button
              onClick={() => handleEditModeChange('draw')}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded transition ${
                editMode === 'draw' ? 'bg-black text-white' : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              <PenTool className="h-4 w-4" />
              Dessiner zone
            </button>
            
            <button
              onClick={() => handleEditModeChange('edit')}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded transition ${
                editMode === 'edit' ? 'bg-black text-white' : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              <Edit3 className="h-4 w-4" />
              Éditer points
            </button>
            
            <button
              onClick={() => handleEditModeChange('duplicate')}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded transition ${
                editMode === 'duplicate' ? 'bg-black text-white' : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              <Copy className="h-4 w-4" />
              Dupliquer zone
            </button>
            
            <button
              onClick={() => handleEditModeChange('move')}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded transition ${
                editMode === 'move' ? 'bg-black text-white' : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              <Move className="h-4 w-4" />
              Déplacer zone
            </button>
            
            <button
              onClick={() => handleEditModeChange('delete')}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded transition ${
                editMode === 'delete' ? 'bg-black text-white' : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              <Trash2 className="h-4 w-4" />
              Supprimer zone
            </button>
            
            <hr className="my-2" />
            
            <button
              onClick={handleExitEditMode}
              className="w-full flex items-center gap-2 px-3 py-2 rounded bg-red-500 text-white hover:bg-red-600 transition"
            >
              <XCircle className="h-4 w-4" />
              Terminer
            </button>
          </div>
        </div>
      )}

{/* Bouton satellite/carte */}
      <div className="absolute top-3 right-14 z-[500]">
        <Button
          variant="outline"
          onClick={() => setIsSatellite(!isSatellite)}
          className="flex items-center gap-2"
          type="button"
        >
          {isSatellite ? <MapIcon className="h-4 w-4" /> : <Satellite className="h-4 w-4" />}
          {isSatellite ? 'Carte' : 'Satellite'}
        </Button>
      </div>

      
      {/* Notification de localisation */}
      <LocationNotification />

      {moveAllFromMapMode && pendingAllMove && (
        <div className="fixed top-0 left-0 right-0 flex justify-center z-[1000] pointer-events-none">
          <div className="mt-8 flex items-center px-8 py-4 bg-yellow-300 text-xl font-bold rounded-2xl shadow-2xl animate-pulse border-4 border-yellow-400 pointer-events-auto">
            <span>Déplacement de tous les chevaux depuis </span>
            <span className="animate-pulse mx-2">{pendingAllMove.name}</span>
            <span className="ml-4 text-base font-normal text-gray-800">(clique sur une zone d'arrivée)</span>
            <button
              className="ml-6 px-4 py-2 text-base bg-white rounded border border-gray-400 font-normal shadow hover:bg-gray-100 hover:border-gray-600 transition pointer-events-auto"
              onClick={() => {
                setMoveAllFromMapMode(false);
                setPendingAllMove(null);
                setMoveTargetZone(null);
              }}
              type="button"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className="h-full relative">
        <MapContainer
          center={[48.8566, 2.3522]}
          zoom={16}
          className="h-full w-full"
          attributionControl={false}
          whenCreated={handleMapCreated}
        >
          <MapWithRefSync />
          <GPSClickHandler 
            selectingGPSLocation={selectingGPSLocation}
            setTechnicalGPSLocation={setTechnicalGPSLocation}
            setSelectingGPSLocation={setSelectingGPSLocation}
            setShowTechnicalTicket={setShowTechnicalTicket}
            pendingTechnicalTicket={pendingTechnicalTicket}
            setPendingTechnicalTicket={setPendingTechnicalTicket}
            navigate={navigate}
          />
          <TileLayer
url={isSatellite 
              ? "https://api.maptiler.com/maps/hybrid/{z}/{x}/{y}.jpg?key=5TmnpuKlF4ppgqb8DHEJ"
              : "https://api.maptiler.com/maps/streets/{z}/{x}/{y}.png?key=5TmnpuKlF4ppgqb8DHEJ"
            }
attribution='MT'
            minZoom={0}
            maxZoom={22}
            tileSize={256}
            zoomOffset={0}
          />
          {/* Zone en cours d'édition */}
          {selectedEditZone && (editMode === 'edit' || editMode === 'move') && (
            <EditableZone
              zone={selectedEditZone}
              editMode={editMode}
              onUpdate={(zoneId, updates) => {
                // Mise à jour temporaire (pour le mode déplacement)
                const updatedZones = zones.map(z => 
                  z.id === zoneId ? { ...z, ...updates } : z
                );
                setZones(updatedZones);
              }}
              onSave={handleSaveZoneEdit}
            />
          )}

          {/* Affichage des zones avec gestion du z-index pour la hiérarchie */}
          {zones.map((zone) => {
            const horsesCount = getHorsesInZone(zone.id).length;
                        // Ne pas afficher la zone si elle est en cours d'édition
            if (selectedEditZone?.id === zone.id && (editMode === 'edit' || editMode === 'move')) {
              return null;
            }
            const isHighlighted = highlightedZone === zone.id;
            
// Calculer le z-index basé sur le niveau (enfants au-dessus des parents)
            // Plus le niveau est élevé, plus la zone est au-dessus
            const zIndexValue = 400 + (zone.level * 100);
            
            return (
              <React.Fragment key={zone.id}>
                <Polygon
                  positions={getLatLngPairs(zone.coordinates)}
pathOptions={{
                    color: isHighlighted ? '#3b82f6' : zone.color,
                    fillColor: isHighlighted ? '#3b82f6' : zone.color,
                    fillOpacity: isHighlighted ? 0.5 : 0.3,
                    weight: isHighlighted ? 4 : 2,
                    dashArray: isHighlighted ? '10, 5' : null,
                    zIndexOffset: zIndexValue
                  }}
                  eventHandlers={{ click: () => handlePolygonClick(zone) }}
                />
                
                {horsesCount > 0 && zone.isOccupable && (
                  <Marker
                    position={getZoneCenter(getLatLngPairs(zone.coordinates))}
                    icon={createHorseCountIcon(
                      horsesCount, 
                      getZoneSurveillanceColor(zone.id),
                      isHighlighted
                    )}
                    eventHandlers={{ click: () => handleZoneBadgeClick(zone) }}
                  />
                )}
              </React.Fragment>
            );
          })}

          {/* Marqueurs permanents des tickets techniques */}
          {technicalTickets.map((ticket) => (
            <Marker
              key={`ticket-${ticket.id}`}
              position={[ticket.gps.lat, ticket.gps.lng]}
              icon={L.divIcon({
                html: `<div style="
                  background-color: ${
                    ticket.priority === 'haute' ? '#dc2626' :
                    ticket.priority === 'moyenne' ? '#f59e0b' :
                    '#10b981'
                  };
                  color: white;
                  border-radius: ${ticket.locationType === 'zone' ? '8px' : '50%'};
                  width: 28px;
                  height: 28px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  border: 2px solid white;
                  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                  font-size: 16px;
                  cursor: pointer;
                ">🔧</div>`,
                className: 'technical-ticket-marker',
                iconSize: [28, 28],
                iconAnchor: [14, 14],
              })}
              eventHandlers={{
                click: () => {
                  setSelectedTechnicalTicket(ticket);
                  setShowTechnicalTicketDetail(true);
                }
              }}
            />
          ))}

          {/* Marqueurs des tickets médicaux (suivent les animaux) */}
          {medicalTickets.map((ticket) => (
            <Marker
              key={`medical-${ticket.id}`}
              position={[ticket.gps.lat, ticket.gps.lng]}
              icon={L.divIcon({
                html: `<div style="
                  background-color: ${
                    ticket.priority === 'haute' ? '#dc2626' :
                    ticket.priority === 'moyenne' ? '#3b82f6' :
                    '#60a5fa'
                  };
                  color: white;
                  border-radius: 6px;
                  width: 28px;
                  height: 28px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  border: 2px solid white;
                  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                  font-size: 14px;
                  cursor: pointer;
                  position: relative;
                ">
                  💉
                  ${ticket.priority === 'haute' ? `
                    <div style="
                      position: absolute;
                      top: -4px;
                      right: -4px;
                      width: 8px;
                      height: 8px;
                      background-color: #ff0000;
                      border-radius: 50%;
                      border: 1px solid white;
                      animation: pulse-red 2s infinite;
                    "></div>
                  ` : ''}
                </div>
                ${ticket.priority === 'haute' ? `
                  <style>
                    @keyframes pulse-red {
                      0% { opacity: 1; transform: scale(1); }
                      50% { opacity: 0.7; transform: scale(1.2); }
                      100% { opacity: 1; transform: scale(1); }
                    }
                  </style>
                ` : ''}`,
                className: 'medical-ticket-marker',
                iconSize: [28, 28],
                iconAnchor: [14, 14],
              })}
              eventHandlers={{
                click: () => {
                  setSelectedTicket({
                    ...ticket,
                    type: 'medical'
                  });
                  setShowMedicalTicketDetail(true);
                }
              }}
            />
          ))}

          {/* Marqueur GPS si sélectionné ou en mode création */}
          {technicalGPSLocation && (
            <Marker
              position={[technicalGPSLocation.lat, technicalGPSLocation.lng]}
              icon={L.divIcon({
                html: `<div style="
                  background-color: ${ticketInfo ? '#8b5cf6' : '#3b82f6'};
                  color: white;
                  border-radius: 50%;
                  width: ${ticketInfo ? '32px' : '24px'};
                  height: ${ticketInfo ? '32px' : '24px'};
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  border: 3px solid white;
                  box-shadow: 0 2px 8px rgba(0,0,0,0.4);
                  font-size: ${ticketInfo ? '18px' : '14px'};
                  ${ticketInfo ? 'animation: pulse 2s infinite;' : ''}
                ">🔧</div>
                ${ticketInfo ? `
                  <style>
                    @keyframes pulse {
                      0% { transform: scale(1); opacity: 1; }
                      50% { transform: scale(1.1); opacity: 0.9; }
                      100% { transform: scale(1); opacity: 1; }
                    }
                  </style>
                ` : ''}`,
                className: 'gps-marker',
                iconSize: ticketInfo ? [32, 32] : [24, 24],
                iconAnchor: ticketInfo ? [16, 16] : [12, 12],
              })}
            />
          )}

          {isDrawing && (
            <FeatureGroup ref={featureGroupRef}>
              <EditControl
                position="bottomright"
                onCreated={handleDrawCreated}
                draw={{
                  rectangle: false,
                  circle: false,
                  circlemarker: false,
                  marker: false,
                  polyline: false,
                  polygon: {
                    allowIntersection: false,
                    drawError: {
                      color: '#e1e100',
                      message: '<strong>Erreur:</strong> les lignes ne peuvent pas se croiser!'
                    },
                    shapeOptions: {
                      color: '#22c55e'
                    }
                  }
                }}
                edit={{ edit: false, remove: false }}
              />
            </FeatureGroup>
          )}
        </MapContainer>

        {/* Overlay du nom du cheval à déplacer (mode carte) */}
        <MovingHorseOverlay />

        {/* Bouton "i" (résumé) - z-index réduit */}
        <div className="absolute top-4 right-4 z-[500]">
          <button
            className="w-11 h-11 flex items-center justify-center rounded-full bg-white shadow hover:bg-blue-100 transition"
            onClick={() => setShowSidebar(true)}
            aria-label="Afficher le résumé"
          >
            <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" />
              <text x="12" y="16" textAnchor="middle" fontSize="12" fill="currentColor">i</text>
            </svg>
          </button>
        </div>

        {/* Sidebar résumé - z-index élevé pour être au-dessus */}
        {showSidebar && (
          <div
            className="fixed inset-0 z-[9000] bg-black bg-opacity-40 flex"
            onClick={() => setShowSidebar(false)}
            tabIndex={-1}
          >
            <div
              className="relative w-[95vw] max-w-md bg-white rounded-xl shadow-lg p-5 sm:ml-16 sm:mt-2 sm:max-w-xs animate-fade-in"
              onClick={e => e.stopPropagation()}
            >
              <button
                className="absolute top-3 right-3 text-gray-600 hover:text-red-500 text-2xl font-bold"
                onClick={() => setShowSidebar(false)}
                aria-label="Fermer le résumé"
              >
                ×
              </button>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Total chevaux:</span>
                  <Badge variant="secondary">{horses.length}</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Total zones:</span>
                  <Badge variant="secondary">{zones.length}</Badge>
                </div>
                
                <div className="mt-4">
  <h4 className="font-medium mb-2">Zones:</h4>
  {buildZoneTree().map(zone => (
    <ZoneTreeItem key={zone.id} zone={zone} />
  ))}
  <GeolocButtonPanel mapRef={mapRef} />
</div>
               


              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de détail du ticket médical */}
      {showMedicalTicketDetail && selectedTicket && selectedTicket.type === 'medical' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FaHandHoldingMedical className="h-5 w-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold">Ticket Médical</h2>
              </div>
              <button
                onClick={() => {
                  setShowMedicalTicketDetail(false);
                  setSelectedTicket(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Animal concerné */}
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <GiHorseHead className="h-5 w-5 text-blue-600" />
                  <div>
                    <span className="font-semibold">{selectedTicket.horseName}</span>
                    {selectedTicket.horseSire && (
                      <span className="text-xs text-gray-500 ml-2">(SIRE: {selectedTicket.horseSire})</span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Zone actuelle : {selectedTicket.currentZoneName}
                </p>
              </div>

              {/* Titre et priorité */}
              <div>
                <h3 className="text-lg font-semibold mb-2">{selectedTicket.title}</h3>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  selectedTicket.priority === 'haute' 
                    ? 'bg-red-100 text-red-800' 
                    : selectedTicket.priority === 'moyenne'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-green-100 text-green-800'
                }`}>
                  Priorité {selectedTicket.priority}
                </span>
              </div>

              {/* Description */}
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Description</h4>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                  {selectedTicket.message || selectedTicket.content}
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowMedicalTicketDetail(false);
                    setSelectedTicket(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Fermer
                </button>
                <button
                  onClick={() => {
                    const id = selectedTicket.animalId || selectedTicket.horseId;
                    if (id) {
                      navigate(`/animaux/${id}`);
                    }
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                >
                  Voir Fiche
                </button>
                <button
                  onClick={() => navigate('/tableau-bord')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Tableau de bord
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de détail du ticket technique */}
      {showTechnicalTicketDetail && selectedTechnicalTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FaWrench className="h-5 w-5 text-purple-600" />
                </div>
                <h2 className="text-xl font-semibold">Ticket Technique</h2>
              </div>
              <button
                onClick={() => {
                  setShowTechnicalTicketDetail(false);
                  setSelectedTechnicalTicket(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Titre et priorité */}
              <div>
                <h3 className="text-lg font-semibold mb-2">{selectedTechnicalTicket.title}</h3>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  selectedTechnicalTicket.priority === 'haute' 
                    ? 'bg-red-100 text-red-800' 
                    : selectedTechnicalTicket.priority === 'moyenne'
                    ? 'bg-orange-100 text-orange-800'
                    : 'bg-green-100 text-green-800'
                }`}>
                  Priorité {selectedTechnicalTicket.priority}
                </span>
              </div>

              {/* Localisation */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Localisation</span>
                </div>
                {selectedTechnicalTicket.zoneName && (
                  <p className="text-sm text-gray-600 mb-1">Zone: {selectedTechnicalTicket.zoneName}</p>
                )}
                {selectedTechnicalTicket.gps && (
                  <p className="text-xs text-gray-500">
                    GPS: {selectedTechnicalTicket.gps.lat.toFixed(6)}, {selectedTechnicalTicket.gps.lng.toFixed(6)}
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Description</h4>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                  {selectedTechnicalTicket.message || selectedTechnicalTicket.description}
                </p>
              </div>

              {/* Métadonnées */}
              <div className="border-t pt-3 space-y-1 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <Clock className="h-3 w-3" />
                  <span>Créé le {new Date(selectedTechnicalTicket.dateCreation).toLocaleDateString('fr-FR')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="h-3 w-3" />
                  <span>Par {selectedTechnicalTicket.acteur || 'Utilisateur'}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowTechnicalTicketDetail(false);
                    setSelectedTechnicalTicket(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Fermer
                </button>
                <button
                  onClick={async () => {
                    if (confirm('Voulez-vous vraiment clôturer ce ticket ?')) {
                      try {
                        const ticketRef = doc(db, 'tickets', selectedTechnicalTicket.id);
                        await updateDoc(ticketRef, {
                          statut: 'cloture',
                          dateCloture: new Date().toISOString()
                        });
                        setShowTechnicalTicketDetail(false);
                        setSelectedTechnicalTicket(null);
                        loadData(); // Recharger pour retirer le marqueur
                        alert('Ticket clôturé avec succès');
                      } catch (error) {
                        console.error('Erreur:', error);
                        alert('Erreur lors de la clôture du ticket');
                      }
                    }
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2 transition-colors"
                >
                  <CheckCircle className="h-4 w-4" />
                  Clôturer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals - tous avec z-index 10000+ pour être au-dessus de tout */}
      <SimpleHorseListModal
        isOpen={showHorseList}
        onClose={() => setShowHorseList(false)}
        zone={selectedZone}
        horses={selectedZone ? getHorsesInZone(selectedZone.id) : []}
        onHorseSelect={handleHorseSelect}
      />

      <SimpleHorseDetailsModal
        isOpen={showHorseDetails}
        onClose={() => setShowHorseDetails(false)}
        horse={selectedHorse}
        zones={zones.filter(z => z.id !== selectedHorse?.zoneId)}
        onMoveHorse={handleMoveHorse}
        onStartMoveFromMap={handleMoveFromMap}
        onReportProblem={() => handleReportProblemClick(selectedHorse)}
      />

      <SimpleMoveConfirmationModal
        isOpen={showMoveConfirmation}
        onClose={() => {
          setShowMoveConfirmation(false);
          setPendingHorseToMove(null);
          setMoveTargetZone(null);
        }}
        horse={selectedHorse || pendingHorseToMove}
        currentZone={zones.find(z => z.id === (selectedHorse ? selectedHorse.zoneId : pendingHorseToMove?.zoneId))}
        targetZone={moveTargetZone}
        onConfirm={confirmMoveHorse}
      />

      <ZoneOptionsModal
        isOpen={showZoneActionModal}
        onClose={() => {
          setShowZoneActionModal(false);
          setZoneActionTarget(null);
        }}
        zone={zoneActionTarget}
        onMoveAllHorses={() => {
          setShowZoneActionModal(false);
          setShowMoveAllModal(true);
          setPendingAllMove(zoneActionTarget);
        }}
        onReportTechnicalIncident={handleReportTechnicalIncident}
        onReportAnimalIncident={handleReportAnimalIncident}
        horsesInZone={zoneActionTarget ? getHorsesInZone(zoneActionTarget.id) : []}
      />
      
      {/* Modal de ticket technique avec GPS */}
      <TechnicalTicketModal
        isOpen={showTechnicalTicket}
        onClose={() => {
          setShowTechnicalTicket(false);
          setTechnicalTicketZone(null);
          setPendingTechnicalTicket(null);
          setTechnicalGPSLocation(null);
        }}
        zone={technicalTicketZone}
        gpsLocation={technicalGPSLocation}
        onSelectLocationFromMap={handleSelectTechnicalLocationFromMap}
        onSelectGPSLocation={handleSelectGPSLocation}
        initialData={pendingTechnicalTicket}
        onSuccess={() => {
          // Réinitialiser les états
          setTechnicalGPSLocation(null);
          console.log('Ticket technique créé avec succès');
          // Recharger les données pour afficher le nouveau marqueur
          loadData();
        }}
      />
      
      <MoveAllHorsesModal
        isOpen={showMoveAllModal}
        onClose={() => {
          setShowMoveAllModal(false);
          setPendingAllMove(null);
          setMoveTargetZone(null);
        }}
        sourceZone={pendingAllMove}
        zones={zones}
        horses={pendingAllMove ? getHorsesInZone(pendingAllMove.id) : []}
        onConfirm={(targetZone) => {
          setPendingAllTargetZone(targetZone);
          setShowMoveAllModal(false);
          setShowMoveAllConfirmation(true);
        }}
        onStartMoveFromMap={() => {
          setShowMoveAllModal(false);
          setMoveAllFromMapMode(true);
        }}
      />
      
      <MoveAllConfirmationModal
        isOpen={showMoveAllConfirmation}
        onClose={() => {
          setShowMoveAllConfirmation(false);
          setMoveTargetZone(null);
          setPendingAllMove(null);
          setPendingAllTargetZone(null);
        }}
        horses={pendingAllMove ? getHorsesInZone(pendingAllMove.id) : []}
        currentZone={pendingAllMove}
        targetZone={pendingAllTargetZone || moveTargetZone}
        onConfirm={async () => {
          const chevaux = getHorsesInZone(pendingAllMove.id);
          const destination = pendingAllTargetZone || moveTargetZone;
          for (const cheval of chevaux) {
            await updateDoc(doc(db, "animaux", cheval.id), { zoneId: destination.id });
          }
          setShowMoveAllConfirmation(false);
          setMoveTargetZone(null);
          setPendingAllMove(null);
          setPendingAllTargetZone(null);
          loadData();
        }}
      />
      
      {/* Modal de signalement de problème */}
      {console.log('Rendering ReportProblemModal section, showReportProblem:', showReportProblem, 'horseToReport:', horseToReport)}
      {showReportProblem && horseToReport && (
        <>
          {console.log('ReportProblemModal SHOULD BE VISIBLE NOW')}
          <ReportProblemModal
            isOpen={true}
            onClose={() => {
              console.log('Closing report problem modal');
              setShowReportProblem(false);
              setHorseToReport(null);
            }}
            horse={horseToReport}
            onSuccess={() => {
              console.log('Report problem success');
              setShowReportProblem(false);
              setHorseToReport(null);
              loadData();
            }}
          />
        </>
      )}
      
      {/* Modal de ticket médical */}
      {showMedicalTicket && horseForMedical && (
        <MedicalTicketModal
          isOpen={true}
          onClose={() => {
            console.log('Closing medical ticket modal');
            setShowMedicalTicket(false);
            setHorseForMedical(null);
          }}
          horse={horseForMedical}
          onSuccess={() => {
            console.log('Medical ticket created successfully');
            setShowMedicalTicket(false);
            setHorseForMedical(null);
            // Pas besoin de recharger les données de la carte
          }}
        />
      )}
    </div>
  );
};

export default HorseMap;